<script lang="ts">
  import { Trash2 } from 'lucide-svelte';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { createRCUChannelMutation, deleteRCUChannelMutation } from '$lib/mutations/rcu_channels';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import {
    dinModuleChannelCount,
    dinModuleLabel,
    isRcuCompatibleLoad,
    compatibleLoadTypesForDin
  } from '$lib/hardware';
  import type { LoadResponse } from '$lib/types/load';
  import type { RCUChannelResponse } from '$lib/types/rcu_channel';

  let { load }: { load: LoadResponse } = $props();

  const unitRcus = rcusQuery();
  const createChannel = createRCUChannelMutation();
  const deleteChannel = deleteRCUChannelMutation();

  let targetKind = $state<'rcu' | 'din'>('rcu');
  let selectedRcuId = $state<number | null>(null);
  let selectedDinId = $state<number | null>(null);
  let channelNumber = $state(1);
  let seeded = $state(false);

  const rcuList = $derived(unitRcus.data ?? []);

  const unitDinModulesAll = dinModulesForUnitQuery();

  const unitDinModules = $derived(
    selectedRcuId ? (unitDinModulesAll.data ?? []).filter((m) => m.rcu === selectedRcuId) : []
  );
  const selectedRcu = $derived(rcuList.find((r) => r.id === selectedRcuId) ?? null);
  const selectedDin = $derived(
    targetKind === 'din' ? (unitDinModules.find((m) => m.id === selectedDinId) ?? null) : null
  );

  const dinModulesForRcu = $derived(
    selectedRcuId
      ? unitDinModules.filter((m) => {
          if (m.rcu !== selectedRcuId) return false;
          if (!load) return true;
          return compatibleLoadTypesForDin(m.module_type).includes(load.load_type);
        })
      : []
  );

  const maxChannels = $derived(
    targetKind === 'rcu'
      ? (selectedRcu?.channel_count ?? 1)
      : selectedDin
        ? dinModuleChannelCount(selectedDin.module_type, selectedDin.dali_bus_count ?? 1)
        : 1
  );

  const objectId = $derived(targetKind === 'rcu' ? selectedRcuId : selectedDinId);

  const saving = $derived(createChannel.isPending || deleteChannel.isPending);

  async function assignChannel() {
    if (!load?.id || !objectId) {
      toast.error('Select RCU or DIN module first');
      return;
    }
    try {
      await createChannel.mutateAsync({
        rcu: objectId,
        channel_number: channelNumber,
        load: load.id
      });
      toast.success('Channel assigned');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }
</script>

<section class="border border-border-default rounded p-3">
  <h3 class="text-sm font-medium text-text-primary mb-3">Channel assignment</h3>

  <div class="grid gap-3 sm:grid-cols-2">
    <div>
      <label class="block text-2xs text-text-muted mb-1" for="target-kind">Target</label>
      <select
        id="target-kind"
        bind:value={targetKind}
        class="w-full h-8 px-2 text-sm border border-border-strong rounded bg-bg-canvas"
      >
        <option value="rcu" disabled={!isRcuCompatibleLoad(load)}>RCU (on/off only)</option>
        <option value="din">DIN module</option>
      </select>
      {#if !isRcuCompatibleLoad(load)}
        <p class="text-2xs text-text-muted mt-1">RCU relay outputs only accept switched loads.</p>
      {/if}
    </div>

    <div>
      <label class="block text-2xs text-text-muted mb-1" for="rcu-select">RCU</label>
      <select
        id="rcu-select"
        bind:value={selectedRcuId}
        class="w-full h-8 px-2 text-sm border border-border-strong rounded bg-bg-canvas"
      >
        <option value={null}>Select RCU…</option>
        {#each rcuList as rcu}
          <option value={rcu.id}>{rcu.name}</option>
        {/each}
      </select>
    </div>

    {#if targetKind === 'din'}
      <div>
        <label class="block text-2xs text-text-muted mb-1" for="din-select">DIN module</label>
        <select
          id="din-select"
          bind:value={selectedDinId}
          class="w-full h-8 px-2 text-sm border border-border-strong rounded bg-bg-canvas"
          disabled={!selectedRcuId}
        >
          <option value={null}>Select module…</option>
          {#each dinModulesForRcu as mod}
            <option value={mod.id}>
              {dinModuleLabel(mod.module_type)} @{mod.address}
            </option>
          {/each}
        </select>
      </div>
    {/if}

    <div>
      <label class="block text-2xs text-text-muted mb-1" for="channel-select">Channel</label>
      <select
        id="channel-select"
        bind:value={channelNumber}
        class="w-full h-8 px-2 text-sm font-mono border border-border-strong rounded bg-bg-canvas"
      >
        {#each Array.from({ length: maxChannels }, (_, i) => i + 1) as num}
          <option value={num}>{num}</option>
        {/each}
      </select>
    </div>
  </div>

  <div class="mt-3 flex justify-end">
    <button
      type="button"
      class="btn-primary text-sm"
      disabled={saving || !objectId}
      onclick={assignChannel}
    >
      Assign channel
    </button>
  </div>
</section>
